// shared/dashboardLayout.ts
/**
 * Dashboard grid layouts for the Operations and Financials tabs.
 *
 * Shared on purpose: the server validates every save with validateLayout, and
 * the client renders, adds widgets and resets from the same constants, so the
 * two can never disagree about what a legal layout is.
 *
 * The grid is one screen by construction (Caleb, 2026-09-29: "you should not
 * have to scroll down to see everything"): 12 columns x 20 rows, with the row
 * height computed from the viewport. A layout that does not fit is never
 * repaired silently. It reads as null, and null means "use the default".
 */
export const GRID_COLS = 12;
export const GRID_ROWS = 20;
/** Gap between widgets, in px (react-grid-layout's margin). */
export const GRID_MARGIN = 10;

export type Board = "ops" | "fin";
export interface LayoutItem { id: string; x: number; y: number; w: number; h: number }
export interface SavedLayout { v: 1; items: LayoutItem[] }
export interface WidgetSize { w: number; h: number; minW: number; minH: number }
export interface WidgetSpec {
  size: WidgetSize;
  /** Only users with the financials grant may have it. */
  gated?: "financials";
  /** Library-only: not part of the default layout. */
  extra?: boolean;
}

export const OPS_WIDGETS: Record<string, WidgetSpec> = {
  tiles:     { size: { w: 12, h: 2, minW: 6, minH: 2 } },
  prospects: { size: { w: 5, h: 13, minW: 3, minH: 5 } },
  sources:   { size: { w: 5, h: 5, minW: 3, minH: 4 } },
  deals:     { size: { w: 4, h: 8, minW: 3, minH: 4 } },
  pfunnel:   { size: { w: 4, h: 5, minW: 3, minH: 4 } },
  touches:   { size: { w: 4, h: 5, minW: 3, minH: 4 } },
  today:     { size: { w: 3, h: 18, minW: 3, minH: 6 } },
  iceberg:   { size: { w: 5, h: 10, minW: 4, minH: 8 }, gated: "financials", extra: true },
  bdScoreboard: { size: { w: 4, h: 6, minW: 3, minH: 4 }, extra: true },
  referrers:    { size: { w: 4, h: 6, minW: 3, minH: 4 }, extra: true },
};

/** One buyer-funnel widget per active deal, id "buyer-funnel:<clientId>". */
export const BUYER_FUNNEL_PREFIX = "buyer-funnel:";
export const buyerFunnelId = (clientId: string) => `${BUYER_FUNNEL_PREFIX}${clientId}`;
const BUYER_FUNNEL_SPEC: WidgetSpec = { size: { w: 4, h: 5, minW: 3, minH: 4 }, extra: true };

export const FIN_WIDGETS: Record<string, WidgetSpec> = {
  statRow:           { size: { w: 12, h: 2, minW: 6, minH: 2 } },
  revenueTimeline:   { size: { w: 7, h: 9, minW: 4, minH: 6 } },
  pipelineIceberg:   { size: { w: 5, h: 18, minW: 4, minH: 8 } },
  pipelineBreakout:  { size: { w: 4, h: 9, minW: 3, minH: 5 } },
  revenueMix:        { size: { w: 3, h: 9, minW: 3, minH: 5 } },
  projectedByClient: { size: { w: 4, h: 9, minW: 3, minH: 5 }, extra: true },
  revenueEntries:    { size: { w: 5, h: 9, minW: 4, minH: 5 }, extra: true },
};

export const DEFAULT_OPS_LAYOUT: SavedLayout = {
  v: 1,
  items: [
    { id: "tiles", x: 0, y: 0, w: 12, h: 2 },
    { id: "prospects", x: 0, y: 2, w: 5, h: 13 },
    { id: "sources", x: 0, y: 15, w: 5, h: 5 },
    { id: "deals", x: 5, y: 2, w: 4, h: 8 },
    { id: "pfunnel", x: 5, y: 10, w: 4, h: 5 },
    { id: "touches", x: 5, y: 15, w: 4, h: 5 },
    { id: "today", x: 9, y: 2, w: 3, h: 18 },
  ],
};

export const DEFAULT_FIN_LAYOUT: SavedLayout = {
  v: 1,
  items: [
    { id: "statRow", x: 0, y: 0, w: 12, h: 2 },
    { id: "revenueTimeline", x: 0, y: 2, w: 7, h: 9 },
    { id: "pipelineIceberg", x: 7, y: 2, w: 5, h: 18 },
    { id: "pipelineBreakout", x: 0, y: 11, w: 4, h: 9 },
    { id: "revenueMix", x: 4, y: 11, w: 3, h: 9 },
  ],
};

export function specFor(board: Board, id: string): WidgetSpec | null {
  if (board === "ops" && id.startsWith(BUYER_FUNNEL_PREFIX)) {
    return id.length > BUYER_FUNNEL_PREFIX.length ? BUYER_FUNNEL_SPEC : null;
  }
  return (board === "ops" ? OPS_WIDGETS : FIN_WIDGETS)[id] ?? null;
}

export function defaultLayout(board: Board): SavedLayout {
  const d = board === "ops" ? DEFAULT_OPS_LAYOUT : DEFAULT_FIN_LAYOUT;
  return { v: 1, items: d.items.map((i) => ({ ...i })) };
}

const isInt = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n);

export function overlaps(items: LayoutItem[]): boolean {
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i], b = items[j];
      if (a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h) return true;
    }
  }
  return false;
}

/** Any number of buyer-funnel ids pass specFor and overlaps is O(n^2), so an oversized body is refused before any work. */
export const MAX_LAYOUT_ITEMS = 64;

/**
 * A clean layout, or null. Unknown widgets (removed from the product) and gated
 * widgets the viewer may not have are DROPPED; anything structurally wrong
 * (bad shape, overlap, out of bounds, under a widget's minimum) is null.
 */
export function validateLayout(raw: unknown, board: Board, opts: { canViewFinancials: boolean }): SavedLayout | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as { v?: unknown; items?: unknown };
  if (r.v !== 1 || !Array.isArray(r.items)) return null;
  if (r.items.length > MAX_LAYOUT_ITEMS) return null;
  const items: LayoutItem[] = [];
  const seen = new Set<string>();
  for (const it of r.items as Record<string, unknown>[]) {
    if (!it || typeof it.id !== "string" || ![it.x, it.y, it.w, it.h].every(isInt)) return null;
    const spec = specFor(board, it.id);
    if (!spec) continue;
    if (spec.gated === "financials" && !opts.canViewFinancials) continue;
    if (seen.has(it.id)) return null;
    const x = it.x as number, y = it.y as number, w = it.w as number, h = it.h as number;
    if (w < spec.size.minW || h < spec.size.minH) return null;
    if (x < 0 || y < 0 || x + w > GRID_COLS || y + h > GRID_ROWS) return null;
    seen.add(it.id);
    items.push({ id: it.id, x, y, w, h });
  }
  if (overlaps(items)) return null;
  return { v: 1, items };
}

export function readLayout(raw: unknown, board: Board, opts: { canViewFinancials: boolean }): SavedLayout {
  return validateLayout(raw, board, opts) ?? defaultLayout(board);
}

/** First top-left slot where a w x h widget fits, scanning rows then columns. */
export function findSpace(items: LayoutItem[], w: number, h: number): { x: number; y: number } | null {
  for (let y = 0; y + h <= GRID_ROWS; y++) {
    for (let x = 0; x + w <= GRID_COLS; x++) {
      const probe: LayoutItem = { id: "__probe", x, y, w, h };
      if (!items.some((it) => overlaps([it, probe]))) return { x, y };
    }
  }
  return null;
}

/** Row height (px) that fits GRID_ROWS rows and their gaps into the container. */
export function rowHeightFor(containerHeight: number): number {
  return Math.max(16, Math.floor((containerHeight - GRID_MARGIN * (GRID_ROWS - 1)) / GRID_ROWS));
}
